import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import scrollToFirstInvalidField from '../functions/scrollToFirstInvalidField';
import getFirstInvalidInput from '../functions/getFirstInvalidInput';
import AnimatedExpandingContainer from './AnimatedExpandingContainer';
import AccordionBlueberryHeader from './AccordionBlueberryHeader';


function AccordionBase({
    isExpanded: controlledIsExpanded,
    onClick,
    isInitiallyExpanded = false,

    HeaderComponent,
    headerProps = {},
    children,

    alwaysRenderChildMarkup = false,

    name,
    disabled = false,
    onAnimationEnd,

    shouldIncludeDivider,

    className,

    ...otherProps
}) {
    const isHeaderBlueberry = HeaderComponent === AccordionBlueberryHeader;

    const isControlled = onClick && controlledIsExpanded != null;
    const [ uncontrolledIsExpanded, setUncontrolledExpanded ] = useState(isInitiallyExpanded);
    const containerRef = useRef(null);

    const shouldRenderAsExpanded = isControlled
        ? controlledIsExpanded
        : uncontrolledIsExpanded;

    const { backgroundColour } = headerProps;

    function handleToggleIsExpanded(e) {
        if (disabled) {
            return;
        }

        onClick?.(e);

        // If controlled, we don't want to do anything when they click
        if (isControlled) {
            return;
        }

        // Only prevent closing, don't prevent opening
        if (uncontrolledIsExpanded && getFirstInvalidInput(containerRef.current)) {
            return scrollToFirstInvalidField(containerRef.current);
        } else {
            setUncontrolledExpanded(!uncontrolledIsExpanded);
        }
    }

    return (
        <Container
            ref={containerRef}
            {...otherProps}
            className={classNames(
                {
                    'rh-mb-1': isHeaderBlueberry,
                },
                className,
            )}
            $backgroundColour={backgroundColour}
        >
            <HeaderComponent
                isExpanded={shouldRenderAsExpanded}
                onClick={handleToggleIsExpanded}
                onKeyPress={handleToggleIsExpanded}
                aria-controls={name
                    ? `${name}-panel`
                    : undefined}
                disabled={disabled}
                {...headerProps}
            />

            <AnimatedExpandingContainer
                id={name
                    ? `${name}-panel`
                    : undefined}
                isVisible={shouldRenderAsExpanded}
                aria-hidden={!shouldRenderAsExpanded}
                alwaysRenderChildMarkup={alwaysRenderChildMarkup}
                isInitiallyExpanded={isInitiallyExpanded}
                disabled={disabled}
                onEnd={() => onAnimationEnd?.(shouldRenderAsExpanded)}
            >
                {children}
            </AnimatedExpandingContainer>

            <If condition={shouldIncludeDivider}>
                <hr className="rh-horizontal-rule rh-m-0" />
            </If>
        </Container>
    );
}


AccordionBase.propTypes = {
    isExpanded: PropTypes.bool,
    onClick: PropTypes.func,
    isInitiallyExpanded: PropTypes.bool,

    HeaderComponent: PropTypes.elementType.isRequired,
    headerProps: PropTypes.object,
    children: PropTypes.node.isRequired,

    alwaysRenderChildMarkup: PropTypes.bool,
    name: PropTypes.string,
    disabled: PropTypes.bool,
    onAnimationEnd: PropTypes.func,

    shouldIncludeDivider: PropTypes.bool,

    className: PropTypes.string,
};


const Container = styled.div`
    background-color: ${props => props.$backgroundColour};
`;

export default observer(AccordionBase);
